// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/**
 * sankey.ts — bipartite Sankey (material → fate). The layout maths is pure and
 * exported so tests/layout.test.ts can assert positions: nodes in bounds, no
 * overlap within a column, and each node's link widths summing to its height.
 */
import { escapeHtml } from '../utils/format';

export interface SankeyNodeIn { key: string; label: string; colour: string; value: number }
export interface SankeyLinkIn { s: number; t: number; value: number }

export interface LaidNode { key: string; label: string; colour: string; value: number; x: number; y: number; w: number; h: number }
export interface LaidLink { s: number; t: number; value: number; width: number; sy0: number; sy1: number; ty0: number; ty1: number; sx: number; tx: number; colour: string }
export interface SankeyLayout { width: number; height: number; left: LaidNode[]; right: LaidNode[]; links: LaidLink[]; scale: number }

export interface SankeyOpts { width?: number; height?: number; nodeWidth?: number; nodePad?: number }

export function sankeyLayout(sources: SankeyNodeIn[], targets: SankeyNodeIn[], links: SankeyLinkIn[], opts: SankeyOpts = {}): SankeyLayout {
  const width = opts.width ?? 760;
  const height = opts.height ?? 460;
  const nodeWidth = opts.nodeWidth ?? 16;
  const pad = opts.nodePad ?? 10;

  const total = sources.reduce((a, s) => a + s.value, 0) || 1;
  const nL = sources.length, nR = targets.length;
  const usableL = height - pad * Math.max(0, nL - 1);
  const usableR = height - pad * Math.max(0, nR - 1);
  const scale = Math.min(usableL, usableR) / total;

  const layoutColumn = (nodes: SankeyNodeIn[], x: number): LaidNode[] => {
    const stackH = nodes.reduce((a, n) => a + n.value * scale, 0) + pad * Math.max(0, nodes.length - 1);
    let y = (height - stackH) / 2;
    const out: LaidNode[] = [];
    for (const n of nodes) {
      const h = n.value * scale;
      out.push({ key: n.key, label: n.label, colour: n.colour, value: n.value, x, y, w: nodeWidth, h });
      y += h + pad;
    }
    return out;
  };

  const left = layoutColumn(sources, 0);
  const right = layoutColumn(targets, width - nodeWidth);

  // Running attachment offsets within each node.
  const lOff = left.map((n) => n.y);
  const rOff = right.map((n) => n.y);
  // Order links so ribbons stack tidily: by source then target.
  const ordered = [...links].filter((l) => l.value > 0).sort((a, b) => a.s - b.s || a.t - b.t);
  const laid: LaidLink[] = [];
  for (const l of ordered) {
    const w = l.value * scale;
    const sy0 = lOff[l.s];
    const ty0 = rOff[l.t];
    lOff[l.s] += w;
    rOff[l.t] += w;
    laid.push({
      s: l.s, t: l.t, value: l.value, width: w,
      sy0, sy1: sy0 + w, ty0, ty1: ty0 + w,
      sx: left[l.s].x + left[l.s].w, tx: right[l.t].x,
      colour: sources[l.s].colour,
    });
  }
  return { width, height, left, right, links: laid, scale };
}

function ribbon(l: LaidLink): string {
  const midX = (l.sx + l.tx) / 2;
  const top = `M${l.sx} ${l.sy0} C${midX} ${l.sy0} ${midX} ${l.ty0} ${l.tx} ${l.ty0}`;
  const down = `L${l.tx} ${l.ty1}`;
  const back = `C${midX} ${l.ty1} ${midX} ${l.sy1} ${l.sx} ${l.sy1}`;
  return `${top} ${down} ${back} Z`;
}

export interface RenderOpts extends SankeyOpts {
  fmt?: (v: number) => string;
  sourceClickAttr?: string; // e.g. 'open-material'
  targetLabel?: (key: string) => string;
  highlightSource?: string | null;
  scroll?: boolean; // wrap in a horizontal scroller (default true)
}

export function renderSankey(sources: SankeyNodeIn[], targets: SankeyNodeIn[], links: SankeyLinkIn[], opts: RenderOpts = {}): string {
  const lay = sankeyLayout(sources, targets, links, opts);
  const fmt = opts.fmt ?? ((v: number) => v.toLocaleString('en-AU'));
  const hi = opts.highlightSource ?? null;

  const linkEls = lay.links
    .map((l) => {
      const src = lay.left[l.s];
      const tgt = lay.right[l.t];
      const dim = hi && src.key !== hi ? 0.12 : 0.4;
      const tip = `${src.label} → ${tgt.label}\n${fmt(l.value)} (${((l.value / (src.value || 1)) * 100).toFixed(0)}% of ${src.label})`;
      return `<path class="sankey-link" d="${ribbon(l)}" fill="${l.colour}" fill-opacity="${dim}" data-src="${escapeHtml(src.key)}" data-tip="${escapeHtml(tip)}"><title></title></path>`;
    })
    .join('');

  const labelPad = 6;
  const nodeEls = (nodes: LaidNode[], side: 'left' | 'right') =>
    nodes
      .map((n) => {
        const clickable = side === 'left' && opts.sourceClickAttr;
        const dataAttr = clickable ? `data-${opts.sourceClickAttr}="${escapeHtml(n.key)}"` : '';
        const labelX = side === 'left' ? n.x + n.w + labelPad : n.x - labelPad;
        const anchor = side === 'left' ? 'start' : 'end';
        const dim = hi && side === 'left' && n.key !== hi ? 0.4 : 1;
        return `<g class="sankey-node" ${dataAttr}${clickable ? ' role="button" style="cursor:pointer"' : ''} data-tip="${escapeHtml(`${n.label}: ${fmt(n.value)}`)}" opacity="${dim}">
          <rect x="${n.x}" y="${n.y}" width="${n.w}" height="${Math.max(1, n.h)}" rx="2" fill="${n.colour}"></rect>
          <text x="${labelX}" y="${n.y + n.h / 2}" text-anchor="${anchor}" dominant-baseline="middle" style="font-size:11px;fill:var(--text-secondary)">${escapeHtml(n.label)}</text>
        </g>`;
      })
      .join('');

  const svg = `<svg class="chart sankey" viewBox="0 0 ${lay.width} ${lay.height}" width="100%" height="${lay.height}" preserveAspectRatio="xMinYMin meet" role="img">
    <g class="sankey-links">${linkEls}</g>
    <g class="sankey-nodes">${nodeEls(lay.left, 'left')}${nodeEls(lay.right, 'right')}</g>
  </svg>`;
  return opts.scroll === false ? svg : `<div class="chart-scroll">${svg}</div>`;
}
